import React, { useMemo, useRef, useEffect, useState } from 'react';
import { getTaskStatusColor } from '../../utils/taskUtils';

const clampDate = (date, min, max) => {
  const t = date.getTime();
  return new Date(Math.min(Math.max(t, min.getTime()), max.getTime()));
};

const daysBetweenInclusive = (start, end) => {
  const ms = 24 * 60 * 60 * 1000;
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);
  return Math.floor((e.getTime() - s.getTime()) / ms) + 1;
};

const useResponsiveDayWidth = (containerRef, daysCount) => {
  const [dayWidth, setDayWidth] = useState(36);
  useEffect(() => {
    const MIN = 180; // wider columns to allow horizontal scroll
    const MAX = 360;
    const GAP = 6;
    const calc = () => {
      const el = containerRef.current;
      if (!el || !daysCount) return;
      const available = el.clientWidth;
      if (!available) return;
      const computed = Math.floor((available - GAP * (daysCount - 1)) / daysCount);
      const clamped = Math.max(MIN, Math.min(MAX, computed));
      setDayWidth(clamped);
    };
    calc();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => calc());
      if (containerRef.current) ro.observe(containerRef.current);
    } else {
      const onResize = () => calc();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    return () => {
      if (ro && containerRef.current) ro.disconnect();
    };
  }, [containerRef, daysCount]);
  return dayWidth;
};

const formatDateShort = (d) => {
  if (!d) return '';
  try {
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return String(d);
  }
};

const TEAM_PALETTE = [
  '#647acb', // matte blue
  '#a28ae5', // matte violet
  '#7ad6ea', // matte sky
  '#46b28a', // matte emerald
  '#f1bb7b', // matte amber
  '#ef767a', // matte red
  '#57c785', // matte green
  '#ed9f67', // matte orange
  '#c6a7e2', // matte purple
  '#5ac8c8', // matte teal
  '#b04632', // matte red-700
  '#6c757d', // matte slate
];

const stringHash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const getTeamColors = (teamName) => {
  if (!teamName || typeof teamName !== 'string') {
    return { bg: '#334155', color: '#ffffff' };
  }
  const idx = stringHash(teamName) % TEAM_PALETTE.length;
  return { bg: TEAM_PALETTE[idx], color: '#ffffff' };
};

const TaskGanttView = ({
  tasks = [],
  selectedDateRange,
  onChangeDateRange,
  onTaskClick
}) => {
  const anchor = useMemo(() => {
    const d = selectedDateRange?.startDate ? new Date(selectedDateRange.startDate) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [selectedDateRange]);

  const monthStart = useMemo(() => {
    const d = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchor]);

  const monthEnd = useMemo(() => {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
    return d;
  }, [anchor]);

  const days = useMemo(() => {
    const arr = [];
    const cur = new Date(monthStart);
    while (cur <= monthEnd) {
      arr.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return arr;
  }, [monthStart, monthEnd]);

  const containerRef = useRef(null);
  const DAY_WIDTH = useResponsiveDayWidth(containerRef, days.length);
  const ROW_HEIGHT = 84; // compact height; some rows can grow taller
  const GRID_GAP = 6;

  const monthLabel = monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const gotoMonth = (offset) => {
    const next = new Date(monthStart.getFullYear(), monthStart.getMonth() + offset, 1);
    const first = new Date(next.getFullYear(), next.getMonth(), 1);
    first.setHours(0, 0, 0, 0);
    const last = new Date(next.getFullYear(), next.getMonth() + 1, 0, 23, 59, 59, 999);
    onChangeDateRange &&
      onChangeDateRange({
        startDate: first.toISOString(),
        endDate: last.toISOString(),
        label: monthLabel
      });
  };

  const getPersonName = (p) => {
    if (!p) return 'Unassigned';
    const first = p.firstName || p.firstname || p.name || '';
    const last = p.lastName || p.lastname || '';
    const full = `${first} ${last}`.trim();
    return full || p.email || 'Unassigned';
  };

  const parseDateSafe = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const rows = useMemo(() => {
    const mapped = (tasks || []).map((t) => {
      const created = parseDateSafe(t.createdAt);
      const started = parseDateSafe(t.startedAt);
      const resolved = parseDateSafe(t.resolvedAt);
      const rejected = parseDateSafe(t.rejectedAt);
      const due = parseDateSafe(t.dueDate);
      const followUp = parseDateSafe(t.followUpOn);
      const teamName = (t.department && (t.department.name || t.department.teamName)) || (typeof t.department === 'string' ? t.department : null);
      // Determine start: prefer started/created; if missing and due is in this month, assume month start
      let start = started || created;
      if (!start) {
        if (due && due >= monthStart && due <= monthEnd) {
          start = new Date(monthStart);
        } else {
          start = due || followUp;
        }
      }
      // If due date is before the computed start, use createdAt or start of current month
      if (due && start && due.getTime() < start.getTime()) {
        start = created || new Date(monthStart);
      }
      if (!start) return null;
      const status = String(t.status || '').toLowerCase();
      let end;
      if (status === 'rejected') {
        end = start;
      } else if (status === 'resolved') {
        end = resolved || start;
      } else {
        end = new Date();
      }
      const MS_DAY = 24 * 60 * 60 * 1000;
      const diffDays = Math.max(1, Math.floor((end.getTime() - start.getTime()) / MS_DAY) + 1);
      const needsStack = diffDays <= 2;
      const clippedStart = clampDate(start, monthStart, monthEnd);
      const clippedEnd = clampDate(end, monthStart, monthEnd);
      const dayIndex = Math.max(0, Math.floor((clippedStart - monthStart) / (24 * 60 * 60 * 1000)));
      const spanDays = Math.max(1, daysBetweenInclusive(clippedStart, clippedEnd));
      return {
        task: t,
        start,
        end,
        created,
        started,
        resolved,
        rejected,
        due,
        teamName,
        dayIndex,
        spanDays,
        startTime: start.getTime(),
        needsStack,
        rowHeight: needsStack ? Math.max(ROW_HEIGHT, 145) : ROW_HEIGHT
      };
    }).filter(Boolean);
    mapped.sort((a, b) => a.startTime - b.startTime);
    // Compute y offsets and total height based on per-row heights
    let y = 0;
    for (let i = 0; i < mapped.length; i++) {
      mapped[i].yOffset = y;
      y += mapped[i].rowHeight + GRID_GAP;
    }
    mapped.totalHeight = Math.max(1, y - GRID_GAP);
    return mapped;
  }, [tasks, monthStart, monthEnd]);

  return (
    <div className="gantt-view-container" style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn-secondary small" onClick={() => gotoMonth(-1)} aria-label="Previous month">Prev</button>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{monthLabel}</div>
          <button className="btn-secondary small" onClick={() => gotoMonth(1)} aria-label="Next month">Next</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#475569' }}>
          {[
            { label: 'Incoming', color: getTaskStatusColor('incoming') },
            { label: 'Pending', color: getTaskStatusColor('pending') },
            { label: 'In-Progress', color: getTaskStatusColor('in-progress') },
            { label: 'Resolved', color: getTaskStatusColor('resolved') },
            { label: 'Rejected', color: getTaskStatusColor('rejected') },
          ].map((item) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: item.color }}></span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ border: '1px solid rgba(226,232,240,0.8)', borderRadius: 12, background: '#fff', maxWidth: '100%' }}>
        <div
          ref={containerRef}
          style={{ overflowX: 'auto', overflowY: 'auto', padding: 12,paddingTop: 0, width: '100%', minWidth: 0, height: 'calc(100vh - 240px)' }}
        >
          {/* Header days row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${days.length}, ${DAY_WIDTH}px)`,
              gap: 0,
              marginBottom: 8,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background: '#f8fafc',
            }}
          >
            {days.map((d) => (
              <div key={d.toISOString()} style={{ fontSize: 12, color: '#64748b', textAlign: 'center', background: '#f8fafc', borderBottom: '1px solid rgba(226,232,240,0.9)' }}>
                <div style={{ fontWeight: 700 }}>{d.getDate()}</div>
                <div>{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
              </div>
            ))}
          </div>

          {/* Grid lines */}
          <div
            style={{
              position: 'relative',
              height: rows.totalHeight || Math.max(rows.length * (ROW_HEIGHT + GRID_GAP), 1),
              display: 'grid',
              gridTemplateColumns: `repeat(${days.length}, ${DAY_WIDTH}px)`,
              gap: GRID_GAP
            }}
          >
            {days.map((d, idx) => (
              <div
                key={`col-${idx}`}
                style={{
                  borderRight: '1px dashed rgba(226,232,240,0.7)'
                }}
              />
            ))}

            {/* Task bars */}
            <div style={{ position: 'absolute', inset: 0 }}>
              {rows.map((row, i) => {
                const left = row.dayIndex * (DAY_WIDTH + GRID_GAP);
                const width = row.spanDays * DAY_WIDTH + (row.spanDays - 1) * GRID_GAP;
                const top = row.yOffset;
                const t = row.task;
                const color = getTaskStatusColor(t.status);
                return (
                  <React.Fragment key={t.id}>
                    <div
                      onClick={() => onTaskClick && onTaskClick(t)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Task ${t.id}`}
                      style={{
                        position: 'absolute',
                        left,
                        top,
                        width,
                        height: row.rowHeight,
                        background: color,
                        borderRadius: 8,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                        {(() => {
                          const parts = [];
                          if (row.created) parts.push(`Created on: ${formatDateShort(row.created)}`);
                          if (row.due) parts.push(`Due on: ${formatDateShort(row.due)}`);

                          if (row.started) parts.push(`Started at: ${formatDateShort(row.started)}`);
                          if (String(t.status || '').toLowerCase() === 'rejected') {
                            if (row.rejected) parts.push(`Rejected on: ${formatDateShort(row.rejected)}`);
                            else parts.push(`Rejected on: ${formatDateShort(row.start)}`);
                          }
                          if (row.resolved) parts.push(`Resolved on: ${formatDateShort(row.resolved)}`);
                          if (parts.length === 0) return null;
                          if (!row.needsStack) {
                            return (
                              <div style={{ fontSize: 10, opacity: 0.95, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {parts.join(' | ')}
                              </div>
                            );
                          }
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 10, opacity: 0.95 }}>
                              {parts.map((p, idx) => (
                                <div key={idx} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p}</div>
                              ))}
                            </div>
                          );
                        })()}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {row.teamName && (() => {
                            const { bg, color } = getTeamColors(row.teamName);
                            return (
                              <span
                                title={`Team: ${row.teamName}`}
                                style={{
                                  //marginLeft: 'auto',
                                  fontSize: 10,
                                  padding: '2px 6px',
                                  borderRadius: 5,
                                  background: bg,
                                  color,
                                  whiteSpace: 'nowrap',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 2
                                }}
                              >
                                {row.teamName}
                              </span>
                            );
                          })()}
                          <span style={{ fontWeight: 600, fontSize: 12 }}>#{t.id}</span>
                         
                          <span style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t.description || t.customer?.name || 'Task'}
                          </span>
                         
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 10, opacity: 0.95 }}>
                          <span>Assigned: {getPersonName(t.assignedEmployee)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 10, opacity: 0.95 }}>
                          <span>Field Staff: {getPersonName(t.supervisor)}</span>
                        </div>
                      </div>
                    </div>
                    {(() => {
                      const due = row.due;
                      if (!due) return null;
                      const gridWidth = days.length * (DAY_WIDTH + GRID_GAP) - GRID_GAP;
                      const dueClamped = clampDate(due, monthStart, monthEnd);
                      const markerDayIndex = Math.max(0, Math.floor((dueClamped - monthStart) / (24 * 60 * 60 * 1000)));
                      const minutesInDay = due.getHours() * 60 + due.getMinutes();
                      const frac = Math.max(0, Math.min(1, minutesInDay / (24 * 60)));
                      let markerLeft = markerDayIndex * (DAY_WIDTH + GRID_GAP) + frac * DAY_WIDTH;
                      markerLeft = Math.max(0, Math.min(gridWidth, markerLeft));
                      return (
                        <div
                          aria-hidden
                          style={{
                            position: 'absolute',
                            left: markerLeft,
                            top,
                            width: 2,
                            height: row.rowHeight,
                            background: 'rgba(220,38,38,0.75)', /* red with transparency */
                            borderRadius: 2,
                            opacity: 0.9
                          }}
                          title={`Due ${formatDateShort(due)}`}
                        />
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskGanttView;


